/**
 * Bot Framework channel→bot JWT validation (spec §8 "Verify"), implemented on
 * WebCrypto + raw fetch only — no botbuilder. Claim checks run before the
 * signature check so obviously-bad tokens never cost a JWKS fetch.
 */
import { Buffer } from "node:buffer";

/** Bot Framework's published OpenID metadata document for channel→bot tokens. */
const DEFAULT_OPENID_METADATA_URL =
  "https://login.botframework.com/v1/.well-known/openidconfiguration";

/** The only issuer Bot Framework channel→bot tokens carry. */
const BOTFRAMEWORK_ISSUER = "https://api.botframework.com";

/** Default clock skew tolerated on `exp`/`nbf` (5 min). */
const DEFAULT_CLOCK_SKEW_MS = 300_000;

/** How long fetched JWKS keys stay cached (24 h). */
const JWKS_CACHE_TTL_MS = 86_400_000;

/** Min interval between unknown-kid forced refetches per metadata URL (60 s). */
const JWKS_FORCED_REFETCH_MIN_INTERVAL_MS = 60_000;

/** Options for {@link validateBotFrameworkJwt}. */
export type JwtValidationOptions = {
  /** The Skaile-published multi-tenant Entra app id — the expected `aud`. */
  appId: string;
  /** Default "https://login.botframework.com/v1/.well-known/openidconfiguration". */
  openIdMetadataUrl?: string;
  /** Default 300_000 (5 min). */
  clockSkewMs?: number;
  fetchImpl?: typeof fetch;
  now?: () => number;
};

/** Outcome of {@link validateBotFrameworkJwt} — `reason` is safe to log, never secret. */
export type JwtValidationResult = { ok: true } | { ok: false; reason: string };

/**
 * In-module JWKS cache — process-global by design, keyed per metadata URL, so
 * multiple adapter instances (gov-cloud vs commercial, tests beside prod)
 * share fetches without colliding. Values map kid → imported verify-only
 * CryptoKey.
 */
const jwksCache = new Map<string, { expiresAt: number; keysByKid: Map<string, CryptoKey> }>();

/** metadata URL → last unknown-kid forced refetch (rate limit; process-global like the cache). */
const lastForcedRefetch = new Map<string, number>();

const fail = (reason: string): JwtValidationResult => ({ ok: false, reason });

/** Decodes one base64url JWT segment into JSON; throws on malformed input. */
function decodeSegment(segment: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(Buffer.from(segment, "base64url").toString("utf8"));
  if (typeof parsed !== "object" || parsed === null) throw new Error("non-object JWT segment");
  return parsed as Record<string, unknown>;
}

/**
 * Resolves the verify key for `kid` via metadata → JWKS, caching all imported
 * keys for 24h. A kid missing from a fresh cache (Microsoft key rotation)
 * forces one refetch, rate-limited to one per minute per metadata URL so a
 * flood of bad-kid tokens cannot hammer the endpoint. Returns undefined for a
 * kid still unknown after that.
 */
async function getSigningKey(
  metadataUrl: string,
  kid: string,
  fetchImpl: typeof fetch,
  now: () => number,
): Promise<CryptoKey | undefined> {
  const cached = jwksCache.get(metadataUrl);
  if (cached && cached.expiresAt > now()) {
    const key = cached.keysByKid.get(kid);
    if (key) return key;
    const last = lastForcedRefetch.get(metadataUrl);
    if (last !== undefined && now() - last < JWKS_FORCED_REFETCH_MIN_INTERVAL_MS) return undefined;
    // Recorded before the fetch so a failing endpoint is not hammered either.
    lastForcedRefetch.set(metadataUrl, now());
  }

  const metadataRes = await fetchImpl(metadataUrl);
  if (!metadataRes.ok) throw new Error(`OpenID metadata fetch failed: ${metadataRes.status}`);
  const metadata = (await metadataRes.json()) as { jwks_uri?: string };
  if (typeof metadata.jwks_uri !== "string") throw new Error("OpenID metadata has no jwks_uri");

  const jwksRes = await fetchImpl(metadata.jwks_uri);
  if (!jwksRes.ok) throw new Error(`JWKS fetch failed: ${jwksRes.status}`);
  const jwks = (await jwksRes.json()) as { keys?: Array<JsonWebKey & { kid?: string }> };

  const keysByKid = new Map<string, CryptoKey>();
  for (const jwk of jwks.keys ?? []) {
    if (typeof jwk.kid !== "string" || jwk.kty !== "RSA") continue;
    try {
      const key = await crypto.subtle.importKey(
        "jwk",
        jwk,
        { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
        false,
        ["verify"],
      );
      keysByKid.set(jwk.kid, key);
    } catch {
      // Unimportable key (foreign alg/params) — skip; other keys stay usable.
    }
  }
  jwksCache.set(metadataUrl, { expiresAt: now() + JWKS_CACHE_TTL_MS, keysByKid });
  return keysByKid.get(kid);
}

/**
 * Validates a Bot Framework channel→bot JWT (RS256 via the published JWKS):
 * Bearer present, alg RS256, iss "https://api.botframework.com", aud === appId,
 * exp/nbf within skew, serviceurl claim === the activity's serviceUrl.
 * JWKS keys cached in-module by kid for 24h; an unknown kid forces one
 * rate-limited refetch (key rotation) before the token is rejected.
 */
export async function validateBotFrameworkJwt(
  authorizationHeader: string | undefined,
  activityServiceUrl: string,
  opts: JwtValidationOptions,
): Promise<JwtValidationResult> {
  const clockSkewMs = opts.clockSkewMs ?? DEFAULT_CLOCK_SKEW_MS;
  const fetchImpl = opts.fetchImpl ?? fetch;
  const now = opts.now ?? Date.now;
  const metadataUrl = opts.openIdMetadataUrl ?? DEFAULT_OPENID_METADATA_URL;

  if (!authorizationHeader?.startsWith("Bearer ")) return fail("missing Bearer token");
  const token = authorizationHeader.slice("Bearer ".length).trim();
  const parts = token.split(".");
  if (parts.length !== 3) return fail("malformed JWT (expected three segments)");

  let header: Record<string, unknown>;
  let payload: Record<string, unknown>;
  try {
    header = decodeSegment(parts[0]);
    payload = decodeSegment(parts[1]);
  } catch {
    return fail("undecodable JWT header or payload");
  }

  if (header.alg !== "RS256") return fail(`unexpected alg "${String(header.alg)}"`);
  if (payload.iss !== BOTFRAMEWORK_ISSUER) return fail(`wrong iss "${String(payload.iss)}"`);
  const aud = payload.aud;
  const audOk = Array.isArray(aud) ? aud.includes(opts.appId) : aud === opts.appId;
  if (!audOk) return fail(`wrong aud "${String(aud)}"`);
  if (typeof payload.exp !== "number") return fail("missing exp claim");
  if (now() > payload.exp * 1000 + clockSkewMs) return fail("token expired");
  if (typeof payload.nbf === "number" && now() < payload.nbf * 1000 - clockSkewMs) {
    return fail("token not yet valid");
  }
  if (payload.serviceurl !== activityServiceUrl) {
    return fail("serviceurl claim does not match the activity serviceUrl");
  }
  if (typeof header.kid !== "string") return fail("missing kid header");

  let key: CryptoKey | undefined;
  try {
    key = await getSigningKey(metadataUrl, header.kid, fetchImpl, now);
  } catch (err) {
    return fail(`signing key resolution failed: ${String(err)}`);
  }
  if (!key) return fail(`unknown signing key "${header.kid}"`);

  const valid = await crypto.subtle.verify(
    "RSASSA-PKCS1-v1_5",
    key,
    Buffer.from(parts[2], "base64url"),
    new TextEncoder().encode(`${parts[0]}.${parts[1]}`),
  );
  return valid ? { ok: true } : fail("invalid signature");
}
